import {
  CreativeLayer,
  FrameRange,
  Interval,
  VideoCreativeLayer,
} from 'content-creation-common/models';

import {
  NUM_PREV_CHUNKS_IN_CACHE,
  NUM_NEXT_CHUNKS_IN_CACHE,
} from '../constants';
import { info } from '../../../util/log';
import { isNotNumber, isNumber } from '../../../util/numbers';
import { isNotNil } from '../../../util/object';
import { isEmpty as isEmptyArray, sortBy } from '../../../util/array';
import { truncateFrameRanges } from '../../../util/frameRange';
import VideoFile from '../models/VideoFile';

/**
 * A caching layer responsible for storing bitmaps for a video file.
 * This layer takes in a VideoFile as an argument and manages the
 * requesting, storing, retrieving and evicting of the frame data.
 * It uses a FrameProcessor to hydrate the cache with frame data.
 */
export default class VideoFrameCache {
  _disableChunkEviction = false;
  _frameBitmapData = new Map();
  _lockedFrameIndices = new Set();
  _numberOfBufferedFrames = null;
  _prevPriorityChunkIndex = null;
  _requestedFrames = new Set();
  _videoCreativeLayers = new Map();

  // TODO(svij) Remove Dependency of "VideoFile". Ideally the cache should be handled
  // by some sort of "cahce chunk" model. ADS-30099
  /** @type {VideoFile} */
  _videoFile;

  /**
   * @param {VideoFile} videoFile the video with frames to load/cache
   * @param {VideoCreativeLayer} videoCreativeLayer
   */
  constructor(videoFile, videoCreativeLayer) {
    if (!VideoFile.isVideoFile(videoFile)) {
      throw new Error('Error constructing a VideoFrameCache');
    }
    this._videoFile = videoFile;

    if (VideoCreativeLayer.isVideoCreativeLayer(videoCreativeLayer)) {
      this._videoCreativeLayers.set(
        videoCreativeLayer.elementId,
        videoCreativeLayer,
      );
    }
  }

  get disableChunkEviction() {
    return this._disableChunkEviction;
  }

  set disableChunkEviction(value) {
    this._disableChunkEviction = value;
  }

  get id() {
    return this._videoFile.mediaIdentifier;
  }

  get numberOfBufferedFrames() {
    return this._numberOfBufferedFrames;
  }

  set numberOfBufferedFrames(value) {
    this._numberOfBufferedFrames = value;
  }

  get videoFile() {
    return this._videoFile;
  }

  get videoCreativeLayers() {
    return this._videoCreativeLayers;
  }

  updateCacheIfNeeded(videoFile, videoCreativeLayer) {
    if (!this._videoFile.equals(videoFile)) {
      this._videoFile = videoFile;
    }

    if (VideoCreativeLayer.isVideoCreativeLayer(videoCreativeLayer)) {
      this._videoCreativeLayers.set(
        videoCreativeLayer.elementId,
        videoCreativeLayer,
      );
    }
  }

  getRelativeFrameIndex(frameIndex) {
    if (isNotNumber(frameIndex)) {
      throw new Error('frameIndex must be of type number');
    }

    // todo(chuti) it is possible to have multiple layers that have different relative frame index
    // we will need to calculate multiple indexes when trimming VIS media is supported because indexes can vary
    for (const videoCreativeLayer of this._videoCreativeLayers.values()) {
      if (videoCreativeLayer.frameRange.containsFrameNumber(frameIndex)) {
        const {
          frameIndex: relativeFrameIndex,
        } = videoCreativeLayer.getRelativeFrameIndicesAndTimesAtFrame(
          frameIndex,
        );
        return relativeFrameIndex;
      }
    }

    return -1;
  }

  getDistanceFromFrameStart(frameIndex) {
    if (isNotNumber(frameIndex)) {
      throw new Error('frameIndex must be of type number');
    }

    if (this._videoCreativeLayers.size === 0) return 0;

    let minFrameStart = null;
    for (const videoCreativeLayer of this._videoCreativeLayers.values()) {
      const { frameRange } = videoCreativeLayer;
      if (isNotNumber(minFrameStart) || frameRange.start < minFrameStart) {
        minFrameStart = frameRange.start;
      }
    }

    return minFrameStart - frameIndex;
  }

  getPrioritizedRelativeFrameRanges(
    requestedFrameRanges,
    forcedFrameRange,
    frameIndex = 0,
    numberOfBufferedFrames = null,
  ) {
    const orderedVideoLayers = this.getOrderedVisibleVideoLayers(
      forcedFrameRange,
    );
    if (isEmptyArray(orderedVideoLayers)) return [];

    // find first index of prioritized frame
    let foundIndex = orderedVideoLayers.findIndex((orderedVideoLayer) =>
      orderedVideoLayer.frameRange.containsFrameNumber(frameIndex),
    );

    const shouldGetBufferedFramesOnly = isNumber(numberOfBufferedFrames);
    // if index is not found and we are within the buffering range, we will extract partial frames
    if (foundIndex === -1) {
      const distanceFromFrameStart = this.getDistanceFromFrameStart(frameIndex);
      // if distance is negative, it means we've passed all the frames
      if (distanceFromFrameStart <= 0) return [];

      if (shouldGetBufferedFramesOnly) {
        const relativeFrameRanges = this.getRelativeFrameRanges(
          requestedFrameRanges,
          forcedFrameRange,
        );
        const numberOfFramesToBuffer = Math.max(
          0,
          numberOfBufferedFrames - distanceFromFrameStart,
        );
        return truncateFrameRanges(relativeFrameRanges, numberOfFramesToBuffer);
      }

      // // default to 0 when we're not getting just buffered frames
      foundIndex = 0;
    }

    const accumulatedFrameRanges = [];
    const lastFrameRanges = [];

    const priorityLayerIndices = orderedVideoLayers.map((e, i) => i);
    // reorder the indices we have to go through based on the prioritized/found index
    priorityLayerIndices.push(...priorityLayerIndices.splice(0, foundIndex));

    // continue at 0 index to the prioritized Index
    for (let i = 0; i < priorityLayerIndices.length; i++) {
      const videoLayerIndex = priorityLayerIndices[i];
      const currentVideoLayer = orderedVideoLayers[videoLayerIndex];

      const { frameEnd, frameStart } = currentVideoLayer;

      // first index will be partial frame range dependent on frameIndex to prioritized
      const firstIndex = i === 0;
      const startFrame = firstIndex ? frameIndex : frameStart;
      const frameRange = new FrameRange(startFrame, frameEnd);

      requestedFrameRanges.forEach((requestedFrameRange) => {
        // if the first range is a partial range,
        // we will add the other part of the range to the end of accumulated frame ranges
        if (firstIndex && frameIndex !== frameStart) {
          const frameRange = new FrameRange(frameStart, frameIndex - 1);
          const intersectedFrameRange = FrameRange.intersection(
            requestedFrameRange,
            frameRange,
          );

          if (FrameRange.isFrameRange(intersectedFrameRange)) {
            lastFrameRanges.push(
              this.getRelativeFrameRange(
                currentVideoLayer,
                intersectedFrameRange,
              ),
            );
          }
        }

        const intersectedFrameRange = FrameRange.intersection(
          requestedFrameRange,
          frameRange,
        );
        if (!FrameRange.isFrameRange(intersectedFrameRange)) return;

        accumulatedFrameRanges.push(
          this.getRelativeFrameRange(currentVideoLayer, intersectedFrameRange),
        );
      });
    }

    // add the last frame range(s)
    accumulatedFrameRanges.push(...lastFrameRanges);

    const mergedFrameRanges = FrameRange.unsortedUnion(accumulatedFrameRanges);
    return shouldGetBufferedFramesOnly
      ? truncateFrameRanges(mergedFrameRanges, numberOfBufferedFrames)
      : mergedFrameRanges;
  }

  getOrderedVisibleVideoLayers(forcedFrameRange) {
    if (this.videoCreativeLayers.size === 0) {
      return [];
    }

    const visibleVideoLayers = this.getVisibleVideoCreativeLayers(
      forcedFrameRange,
    );

    return sortBy(visibleVideoLayers, 'frameStart');
  }

  /*
   * get the relative frame range based on the video creative layer
   * @param  {VideoCreativeLayer} videoCreativeLayer
   * @param  {FrameRange} frameRange
   * @return {FrameRange}
   */
  getRelativeFrameRange(videoCreativeLayer, frameRange) {
    const {
      frameIndex: startIndex, // frame index of media relative to the given index (intersectedFrameRange.start)
    } = videoCreativeLayer.getRelativeFrameIndicesAndTimesAtFrame(
      frameRange.start,
    );

    const {
      endIndex, // the last frame of the element
      frameIndex, // frame index of element relative to the given index (intersectedFrameRange.end)
      totalFrames: videoTotalFrames, // total number of frames of the element
    } = videoCreativeLayer.getRelativeFrameIndicesAndTimesAtFrame(
      frameRange.end,
    );

    // getRelativeFrameIndicesAndTimesAtFrame calculate for looping frameIndex,
    // if the layer is looped, we will use the end index instead of the looped frame index
    const finalEndIndex =
      videoTotalFrames < frameRange.length ? endIndex : frameIndex;

    return new FrameRange(startIndex, finalEndIndex);
  }

  getRelativeFrameRanges(requestedFrameRanges, forcedFrameRange) {
    const visibleVideoLayers = this.getOrderedVisibleVideoLayers(
      forcedFrameRange,
    );

    if (isEmptyArray(visibleVideoLayers)) {
      return [];
    }

    const accumulated = [];
    requestedFrameRanges.forEach((requestedFrameRange) => {
      visibleVideoLayers.forEach((videoCreativeLayer) => {
        const intersectedFrameRange = FrameRange.intersection(
          videoCreativeLayer.frameRange,
          requestedFrameRange,
        );

        // checks to see if the layer needs buffering by comparing
        // its frame range to expected buffer frame range
        if (!FrameRange.isFrameRange(intersectedFrameRange)) return;

        accumulated.push(
          this.getRelativeFrameRange(videoCreativeLayer, intersectedFrameRange),
        );
      });
    });

    return FrameRange.unsortedUnion(accumulated);
  }

  /**
   * @function getUnavailableChunkFrameRanges
   * returns unavailable frame ranges based on chunk indexes of request frame ranges
   * @param  {Array<FrameRange>} frameRangeList {description}
   * @param  {boolean} checkRequestedFrames check whether the bitmap frame has been requested
   * @return {Array<FrameRange>} List of frame ranges that are unavailable
   */
  getUnavailableFrameRanges = (
    frameRangeList,
    checkRequestedFrames = false,
  ) => {
    const unavailableFrameRanges = [];
    frameRangeList.forEach((frameRange) => {
      const startFrameCacheChunkIndex = this._videoFile.getChunkIndex(
        frameRange.start,
      );

      // get min of either calculated chunk index based on frame end or max chunk index
      const endFrameCacheChunkIndex = Math.min(
        this._videoFile.getChunkIndex(frameRange.end),
        this._videoFile.getTotalChunkCount() - 1,
      );

      let currIndex = startFrameCacheChunkIndex - 1;
      let done = false;
      do {
        currIndex++;
        const chunkFrameRange = this._videoFile.getFrameRangeOfChunk(currIndex);

        if (chunkFrameRange === null) {
          done = true;
        }

        if (
          isNotNil(chunkFrameRange) &&
          !this.hasBitmapDataForFrameRanges(
            [chunkFrameRange],
            checkRequestedFrames,
          )
        ) {
          unavailableFrameRanges.push(chunkFrameRange);
        }
        if (currIndex === this._videoFile.totalChunks) {
          currIndex = 0;
        }
        if (currIndex === endFrameCacheChunkIndex) {
          done = true;
        }
      } while (!done);
    });
    return Interval.unsortedUnion(unavailableFrameRanges);
  };

  /**
   * @function putFrameBitmapData
   * @param  {Number} frameIndex
   * @param  {ImageBitmap} imageBitmap
   */
  putFrameBitmapData = (frameIndex, imageBitmap) => {
    this._frameBitmapData.set(frameIndex, imageBitmap);
  };

  /**
   * @function removeFrameBitmapData
   * @param  {Number} frameIndex
   */
  removeFrameBitmapData = (frameIndex) => {
    this._frameBitmapData.delete(frameIndex);
    this._requestedFrames.delete(frameIndex);
  };

  /**
   * Returns the bitmap data for a specified frame,
   * if it's not available, it will request it.
   * @function getFrameBitmapData
   * @param {int} frameIndex
   * @param {boolean} returnClosestFrame
   * @returns {ImageData | null}
   */
  getFrameBitmapData = (frameIndex, returnClosestFrame = false) => {
    // If the frame has been extracted, return it.
    if (this._frameBitmapData.has(frameIndex)) {
      return this._frameBitmapData.get(frameIndex);
    }

    // Otherwise, prioritize the requested frame and find the nearest preceeding frame.
    if (
      returnClosestFrame &&
      this._frameBitmapData.size > 0 &&
      frameIndex > 0
    ) {
      // Otherwise, prioritize the requested frame and find the nearest preceeding frame.
      let lastIndex = 0;
      for (let i = frameIndex - 1; i >= 0; i--) {
        if (this._frameBitmapData.has(i)) {
          lastIndex = i;
          break;
        }
      }
      return this._frameBitmapData.get(lastIndex);
    }

    return null;
  };

  /**
   * Returns an array of visible video creative layers
   * @param {FrameRange} forcedFrameRange will force video creative layers
   * to be returned if it intersect with the forced FrameRange
   * @returns {array<VideoCreativeLayer>} list of visible video creative layer
   */
  getVisibleVideoCreativeLayers = (forcedFrameRange = null) => {
    if (this.videoCreativeLayers.size === 0) {
      return [];
    }

    const hasForcedFrameRange = FrameRange.isFrameRange(forcedFrameRange);
    // only extract visible layers
    const visibleVideoLayers = [];
    this.videoCreativeLayers.forEach((videoCreativeLayer) => {
      if (
        hasForcedFrameRange &&
        videoCreativeLayer.frameRange.intersects(forcedFrameRange)
      ) {
        visibleVideoLayers.push(videoCreativeLayer);
        return;
      }

      // grab its parent
      const rootParent = CreativeLayer.getRootParent(videoCreativeLayer);
      // check if parent is 'skipped'
      // todo() see if it's worth putting skipped shot info on the child element
      // instead of having to read from the root parent
      if (CreativeLayer.isCreativeLayer(rootParent)) {
        const {
          element: { meta },
        } = rootParent;
        if (isNotNil(meta) && meta.shotSkipped) return;
      }

      if (!videoCreativeLayer.visible) return;

      visibleVideoLayers.push(videoCreativeLayer);
    });

    return visibleVideoLayers;
  };

  /**
   * Returns true if the bitmap data exists for a specified frame.
   * @param {int} frameIndex
   * @returns {boolean} true if cache contains frame index.
   */
  hasFrameBitmapData = (frameIndex) => {
    return this._frameBitmapData.has(frameIndex);
  };

  /**
   * Returns true if all the bitmap data exists for all frames in the frame ranges.
   * @param {FrameRange[]} frameRanges to check for bitmaps
   * @param {boolean} checkRequestedFrames
   * @returns {boolean} true if cache contains all frame indices.
   */
  hasBitmapDataForFrameRanges = (frameRanges, checkRequestedFrames = false) => {
    return frameRanges.every((frameRange) => {
      for (let i = frameRange.start; i <= frameRange.end; i++) {
        const hasFrame =
          this.hasFrameBitmapData(i) ||
          (checkRequestedFrames && this._requestedFrames.has(i));
        if (!hasFrame) return false;
      }
      return true;
    });
  };

  /**
   * Prioritize a frame in the frame cache. This schedules it for download
   * and extraction by the frame processor and also informs which frames to
   * evict from the cache.
   * @param {int} globalFrameIndex of frame to prioritize in the frame cache
   * @param {FrameRange[]} requestedFrameRanges
   * @param {FrameRange} forcedFrameRange
   */
  prioritizeFrameIndex = (
    globalFrameIndex,
    requestedFrameRanges,
    forcedFrameRange,
  ) => {
    const relativeFrameIndex = this.getRelativeFrameIndex(globalFrameIndex);
    const nextPriorityChunkIndex = this._videoFile.getChunkIndex(
      relativeFrameIndex,
    );

    // If the chunk index hasn't changed, skip eviction.
    if (nextPriorityChunkIndex !== this._prevPriorityChunkIndex) {
      this._evictChunksAroundFrame(
        globalFrameIndex,
        requestedFrameRanges,
        forcedFrameRange,
      );
    }
    this._prevPriorityChunkIndex = nextPriorityChunkIndex;
  };

  /**
   * Lock a frame in the frame cache. This will protect the
   * frame from being evicted after it is entered into the cache.
   * @param {int} frameIndex of frame to protect
   */
  lockFrameIndex = (frameIndex) => {
    this._lockedFrameIndices.add(frameIndex);
  };

  /**
   * Unlock a frame in the frame cache. This allows a frame to be
   * removed during a cache eviction, if it had been previously locked.
   * @param {int} frameIndex of frame to unlock
   */
  unlockFrameIndex = (frameIndex) => {
    this._lockedFrameIndices.delete(frameIndex);
  };

  /**
   * Check if a frame is locked
   * @param {int} frameIndex
   */
  hasLockedFrameIndex = (frameIndex) => {
    return this._lockedFrameIndices.has(frameIndex);
  };

  cleanup = () => {
    info(`Cleaning up frame cache for ${this._videoFile.mediaIdentifier}`);
    this._lockedFrameIndices.clear();
    const totalChunks = this._videoFile.getTotalChunkCount();
    for (let i = 0; i < totalChunks; i++) {
      this._evictChunk(i);
    }

    this._videoFile = null;
  };

  clear() {
    this._frameBitmapData.clear();
    this._lockedFrameIndices.clear();
    this._requestedFrames.clear();
    this._prevPriorityChunkIndex = null;
  }

  removeUnusedFrames = (playableFrameRanges) => {
    // convert playable frame ranges into set of frames
    const framesNeeded = new Set();
    playableFrameRanges.forEach((playableFrameRange) => {
      for (let i = playableFrameRange.start; i <= playableFrameRange.end; i++) {
        framesNeeded.add(i);
      }
    });

    this._frameBitmapData.forEach((bitmap, frameIndex) => {
      if (!framesNeeded.has(frameIndex)) {
        this.removeFrameBitmapData(frameIndex);
      }
    });
  };

  setRequestedFrames = (frameRanges) => {
    frameRanges.forEach((frameRange) => {
      for (let i = frameRange.start; i <= frameRange.end; i++) {
        this._requestedFrames.add(i);
      }
    });
  };

  /**
   * Removes bitmaps for frames of the video chunk and
   * notifies the frame processor to unload the chunk.
   * @param {int} chunkIndex to purge
   */
  _evictChunk = (chunkIndex) => {
    const frameOffset = this._videoFile.framesPerChunk * chunkIndex;
    for (let i = 0; i < this._videoFile.framesPerChunk; i++) {
      const frameIndex = i + frameOffset;
      if (this._lockedFrameIndices.has(frameIndex)) continue;
      this.removeFrameBitmapData(frameIndex);
    }
  };

  evictChunks = (startIndex, endIndex) => {
    if (isNotNumber(startIndex) || isNotNumber(endIndex)) {
      throw new Error('startIndex and endIndex must be of type number');
    }

    const maxIndex = this._videoFile.getLastChunkIndex();
    if (endIndex > maxIndex) {
      throw new Error(
        `endIndex (${endIndex}) is greater than total number of chunks (${maxIndex})`,
      );
    }

    for (let i = startIndex; i <= endIndex; i++) {
      this._evictChunk(i);
    }

    info(`evict chunk range ${startIndex}, ${endIndex}`);
  };

  /**
   * Evict chunks that are outside of the cache window surrounding a frame index.
   * @param {int} frameIndex of the origin of the cache range.
   * @param {FrameRange[]} requestedFrameRanges
   * @param {FrameRange} forcedFrameRange
   */
  _evictChunksAroundFrame = (
    frameIndex,
    requestedFrameRanges,
    forcedFrameRange,
  ) => {
    if (this._disableChunkEviction) return;

    const chunksRangesToEvict = this._getChunkRangesToEvict(
      frameIndex,
      requestedFrameRanges,
      forcedFrameRange,
    );
    chunksRangesToEvict.forEach((chunkRange) => {
      this.evictChunks(chunkRange.start, chunkRange.end);
    });
  };

  _getPlayableChunkIndices = (playableFrameRanges) => {
    if (isEmptyArray(playableFrameRanges)) return [];
    return playableFrameRanges.reduce(
      (playableChunkIndicies, playableFrameRange) => {
        const startChunkIndex = this._videoFile.getChunkIndex(
          playableFrameRange.start,
        );
        const endChunkIndex = this._videoFile.getChunkIndex(
          playableFrameRange.end,
        );
        for (let i = startChunkIndex; i <= endChunkIndex; i++) {
          playableChunkIndicies.push(i);
        }
        return playableChunkIndicies;
      },
      [],
    );
  };

  /**
   * Gets a set of 'ranges' for chunk eviction.
   * @param {int} frameIndex origin of the cache range to preserve.
   * @param {FrameRange[]} requestedFrameRanges list of frame ranges that has been requested
   * @param {FrameRange} forcedFrameRange
   * @returns {[[start, end]]} An array of ranges of chunk indices.
   */
  _getChunkRangesToEvict = (
    frameIndex,
    requestedFrameRanges,
    forcedFrameRange,
  ) => {
    // if frame Index is invalid, return the entire chunk range to evict
    if (frameIndex === -1) {
      return [new Interval(0, this._videoFile.getLastChunkIndex())];
    }

    const prioritizedFrameRanges = this.getPrioritizedRelativeFrameRanges(
      requestedFrameRanges,
      forcedFrameRange,
      frameIndex,
    );
    const prioritizedChunkIndices = this._getPlayableChunkIndices(
      prioritizedFrameRanges,
    );

    // first, add 5 chunks from the prioritized chunk
    const prioritizedChunks = [];
    for (let i = 0; i <= NUM_NEXT_CHUNKS_IN_CACHE; i++) {
      const index = prioritizedChunkIndices[i];
      prioritizedChunks.push(new FrameRange(index, index));
    }

    // add 2 chunks previous to the prioritized chunk
    const numberOfChunks = prioritizedChunkIndices.length;
    const lastIndex = numberOfChunks - 1;
    for (let i = lastIndex; i > lastIndex - NUM_PREV_CHUNKS_IN_CACHE; i--) {
      const index = prioritizedChunkIndices[i];
      prioritizedChunks.push(new FrameRange(index, index));
    }

    const rangesOfChunksToPreserve = FrameRange.union(prioritizedChunks);
    const videoFileInterval = new Interval(
      0,
      this._videoFile.getTotalChunkCount() - 1,
    );
    info(
      `We like the following ranges and will keep them: ${rangesOfChunksToPreserve}`,
    );

    return videoFileInterval.relativeComplementIn(rangesOfChunksToPreserve);
  };

  static isVideoFrameCache(inputObject) {
    return inputObject instanceof VideoFrameCache;
  }
}
